import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The sixth written page. Everything below comes from the four-part full-page
// capture of digitopia.co/customers/yazaki/ sent on 2026-08-28 — "birebir tıpa
// tıp aynısını tasarla" — with the page's own photography: the robotic assembly
// line in the header, Yazaki's headquarters between the Challenge and the
// Solution, and two pictures of what they make and how. Those last two fill the
// halves of the Solution and Value panels that are blank in the capture, which
// is where Intel AI's fourth photograph went for the same reason a day earlier.
// The closing quote is not a `section`: Tekin Gülşen's words have been in
// `content/testimonials.ts` keyed to this slug since the Hall of Fame shipped,
// mark and portrait included, and `StoryBody` draws that card — the testimonial
// template the same request asked for at the foot of the page.
export const added = story('yazaki', 'Yazaki', {
  doc: '18d08baa-267a-4629-affb-03a17339b060-Yazaki and Digitopia.pdf',
  lede: [
    'Yazaki now has the directional insights it needs to accelerate on the road toward deeper '
    + 'digital transformation.',
  ],
  heroPhoto: {
    src: '/photo/yazaki-robotic-vehicle-assembly.webp',
    alt: 'Two humanoid robots working on a white car body, opened up on a lit assembly line.',
  },
  intro: [
    'Yazaki Europe Middle East Africa (Y-EMEA) and Yazaki North and Central America (YNCA) are '
    + 'key parts of the global Yazaki Group. Yazaki brings next generation automotive products '
    + 'to their customers by supplying electrical distribution systems, components, electronics '
    + 'and instrumentations and high voltage products. The two regional corporations recently '
    + 'embarked on a digital maturity journey with Digitopia. Determined to keep pace with the '
    + 'rapid technological shifts in the automotive sector, Yazaki decided to take a critical '
    + 'look at their own digital readiness in the two regions. The outcome of the analysis has '
    + 'already proven to be an important catalyst, shedding light on areas of opportunity. Mr. '
    + 'Tekin Gülşen, VP of IT Y-EMEA, has expressed great satisfaction with the process and '
    + 'early business outcomes.',
  ],
  sections: [
    {
      heading: 'The Challenge',
      centred: true,
      body: [
        'As a major player in the global automotive supply chain, Yazaki integrates complex '
        + 'engineering, manufacturing, and logistics operations. Their success depends on '
        + 'seamless coordination across multiple geographies and high-value collaborations '
        + 'with automotive OEMs. With digital innovation taking center stage in the automotive '
        + 'industry, spanning connected vehicles, electrification, and Industry 4.0, Yazaki '
        + 'recognized the urgency to elevate their digital strategy.',
        'Before engaging Digitopia, Yazaki’s leadership was aware of the need to measure '
        + 'digital maturity, and they were looking for a robust framework to assess the '
        + 'nuances of different regional priorities. By splitting the exercise into Y-EMEA and '
        + 'YNCA, Yazaki aimed to capture region-specific data, tailor recommendations, and '
        + 'accelerate decision-making to remain a frontrunner in this highly competitive space.',
      ],
      photo: {
        src: '/photo/yazaki-headquarters-building.webp',
        alt: 'Yazaki’s headquarters: a grey office building carrying the Yazaki mark, under a '
          + 'blue sky, with cherry blossom in the garden in front of it.',
      },
    },
    {
      heading: 'The Solution: Mapping Digital Maturity with Digitopia',
      band: true,
      photo: {
        src: '/photo/yazaki-wiring-harness.webp',
        alt: 'A complete Yazaki wiring harness laid out flat on a white floor, with hundreds of '
          + 'looms and connectors running to a fuse box.',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'Digitopia’s Digital Maturity Index (DMI) provided a good foundation for Yazaki’s '
        + 'further digital development. The engagement unfolded across two parallel tracks for '
        + 'Y-EMEA and YNCA, ensuring localized insights while maintaining consistency at the '
        + 'group level.',
        'Key elements of the Digitopia approach included:',
      ],
      ordered: true,
      items: [
        {
          lead: 'Comprehensive Assessment',
          text: 'By applying the DMI, Digitopia evaluated Yazaki’s technological capabilities, '
            + 'processes, and cultural readiness in the digital area. This in-depth analysis '
            + 'gave Yazaki leadership a precise snapshot of each region’s strengths and '
            + 'improvement areas.',
        },
        {
          lead: 'Tailored Recommendations',
          text: 'The DMI results formed the basis for a clear set of strategic actions. '
            + 'Recognizing that no two regions face identical challenges, Digitopia delivered '
            + 'a customized roadmap for both EMEA and NCA, addressing region-specific business '
            + 'goals.',
        },
        {
          lead: 'Creating Visibility & Alignment',
          text: 'This dual-region measurement created a clear picture of the current situation '
            + 'for each regional team. By comparing maturity scores and pinpointing gaps, '
            + 'Yazaki’s leaders could unify cross-functional stakeholders around a shared '
            + 'vision.',
        },
      ],
    },
    {
      heading: 'Value & Benefits',
      band: true,
      photo: {
        src: '/photo/yazaki-factory-automation.webp',
        alt: 'Robot arms and handling machinery on a production line behind yellow safety '
          + 'railings.',
      },
      photoSide: 'left',
      items: [
        {
          lead: 'Clarity and Alignment Across Regions',
          text: 'Conducting two parallel measurements helped Yazaki achieve clarity on how '
            + 'each region stacks up against digital best practices.',
        },
        {
          lead: 'Roadmap for Sustainable Transformation',
          text: 'Even at this early stage, Yazaki’s leadership feels more confident about '
            + 'moving forward on their digital journey.',
        },
      ],
    },
    {
      heading: 'Conclusion',
      band: true,
      centred: true,
      body: [
        'Yazaki’s first Digital Maturity Index assessment with Digitopia is an important step '
        + 'in their pursuit of excellence. By conducting separate analyses for Y-EMEA and '
        + 'YNCA, Yazaki gained clear insights, ignited essential alignment, and built a shared '
        + 'sense of the current status across regions.',
        'While the journey is still young, the energy sparked by Digitopia’s structured '
        + 'approach is already producing dividends in clarity, agility, and collaboration. '
        + 'Yazaki now has the directional insights it needs to accelerate on the road toward '
        + 'deeper digital transformation, confident that the DMI framework, supported by '
        + 'dedicated stakeholders, will guide them along the way.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a four-part full-page capture of '
    + `${STORY_SOURCE}/yazaki/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the two region names, '
    + 'which the reference writes as “Y-EMEA” and “YNCA” in the opening band and as “EMEA” '
    + 'and “NCA” inside the Solution, and Mr Gülşen’s title, which reads “VP of IT Y-EMEA” in '
    + 'that band and “VP of IT Y-EMEA and Global Applications” under his quote. Two spellings '
    + 'were corrected rather than copied: the reference sets “Y- EMEA” with a space once, and '
    + 'writes “Tekin Gulsen” without its Turkish letters where the quote below it and the rest '
    + 'of this site write Gülşen. Four deliberate differences from the reference, each of '
    + 'them a template-wide decision rather than this page’s: the header eyebrow reads '
    + '“Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS STORIES”; the '
    + 'headline is the company name, so the reference’s own “Yazaki’s Digital Transformation '
    + 'Success Story” survives only in the standfirst under it — say the word and it can '
    + 'become this page’s H1; and the reference repeats a “Read More” button in the header and '
    + 'inside the Solution and Value panels, which here is the one “Read the full story (PDF)” '
    + 'button in the header, pointing at the document; and the quote card at the foot is the '
    + 'Hall of Fame template as asked for, so its eyebrow is the company where the reference '
    + 'sets the person’s name — his name and title are on the line under the quote either '
    + 'way. Two things to look at rather than '
    + 'decide. The right half of the reference’s Solution panel and the left half of its Value '
    + '& Benefits panel are both blank in the capture, and the two photographs supplied with '
    + 'it that the capture never shows — the wiring harness and the production line — are '
    + 'placed in them, on the assumption that they are what did not load; move or drop either '
    + 'if that is wrong. And “Value & Benefits” is set as two bulleted points because its two '
    + 'bold labels are exactly the shape this template’s lists carry, where the reference sets '
    + 'them as two paragraphs with no markers. Lastly, digitopia.co’s *other* Yazaki page — '
    + 'imported/reference/en/yazaki-digital-maturity-success-story.json, served at its own '
    + 'address rather than under /customers/ — tells the same engagement in different words; '
    + 'nothing from it is used here, and /yazaki-digital-maturity-success-story still '
    + 'redirects to the Customers index rather than to this page, which somebody may want to '
    + 'change. One production note: all four photographs are 750–900px originals, which is '
    + 'what the live site serves, so they are drawn at that width and will look soft on a '
    + 'high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 3
